// React Dependencies
import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';

// External Dependencies
import { TextField, IconButton, Dialog, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';

// Internal Dependencies
import OverLoading from '../../../components/Loading';
import MText from '../../../components/Common/MText';
import MButton from '../../../components/MButton';
import Colours from '../../../styles/colours';
import ApiService from '../../../services/apiService';
import { formatDateToDayMonthNameYear, formatDateWithTimetoString } from '../../../common/util';
import Icons from '../../../components/Common/Icons';
import { EmailIsValid, IsEmpty, IsNumber } from '../../../common/checks';
import { Url } from '../../../common/constants';

const LoyaltyCardsCustomers = (props) => {
  const { pageId } = useParams();
  const history = useHistory();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loyaltyCards, setLoyaltyCards] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleSearch = async () => {
    try {
      setLoading(true);
      setLoyaltyCards([]);

      if (IsEmpty(email)) {
        return toast.error('Please enter an email');
      }

      if (!EmailIsValid(email)) {
        return toast.error('Please enter a valid email');
      }

      const loyaltyCardData = await ApiService.getShopperLoyaltyCardsByEmail({ pageId, email });
      if (IsEmpty(loyaltyCardData)) {
        toast.info('No loyalty cards found for this email');
      }

      setLoyaltyCards(loyaltyCardData || []);
    } catch (err) {
      toast.error(err.message);
      setLoyaltyCards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (item) => {
    setLoading(true);
    try {
      const stamps = await ApiService.getStampsByShopperLoyaltyCardId({
        pageId,
        shopperLoyaltyCardId: item.shopperLoyaltyCardId,
      });

      setSelectedItem({ ...item, stamps: stamps || [] });
      setModalOpen(true);
    } catch (err) {
      toast.error(`Failed to fetch stamps for ${item.loyaltyCardCode || ''}${item.shopperLoyaltyCardId || ''}${item.shopperLoyaltyCardIdCode || ''}`);
      setSelectedItem(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      {loading && <OverLoading />}
      <div className="container-fluid mt-4">
        <div className="d-flex align-items-center">
          <IconButton style={{ width: 50, height: 50 }} onClick={() => history.push(Url.LoyaltyCards.replace(':pageId', props.match.params.pageId))}>
            <Icons.ArrowBack colour={Colours.Gray1} />
          </IconButton>
          <MText size={16} bold text="Search Customer’s Card" />
        </div>

        <div className="mb-4 p-4 bg-white rounded shadow w-100">
          <div className="w-100" style={{ maxWidth: 400 }}>
            <MText size={12} colour={Colours.Gray} text="Enter customer’s email" bold className="mb-1 d-block" />
            <div className="d-flex align-items-center">
              <TextField
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder="Customer email"
                size="small"
                className="flex-grow-1"
                inputProps={{
                  style: { padding: '6px 8px', fontSize: 12, height: 22 },
                }}
              />
              <MButton
                text="Search"
                className="px-3 py-1 ml-2"
                onClick={handleSearch}
                colour={Colours.Primary}
                textColour={Colours.White}
                style={{
                  fontSize:   12,
                  minHeight:  32,
                  whiteSpace: 'nowrap',
                }}
              />
            </div>
          </div>
        </div>

        {!IsEmpty(loyaltyCards) && (
          <MaterialTable
            title=""
            data={loyaltyCards}
            options={{
              pageSize:            25,
              pageSizeOptions:     [25, 50, 80],
              actionsColumnIndex:  -1,
              emptyRowsWhenPaging: false,
              search:              false,
              sorting:             false,
              headerStyle:         { fontSize: 12, fontWeight: 'bold' },
              rowStyle:            { fontSize: 12 },
            }}
            components={{ Toolbar: () => null }}
            columns={[
              { title: 'Email', field: 'email', width: '15%' },
              { title: 'Loyalty Program', field: 'loyaltyCardName', width: '15%' },
              {
                title:  'Customer Card ID',
                field:  'customerCardId',
                width:  '15%',
                render: (d) => `${d.loyaltyCardCode || ''}${d.shopperLoyaltyCardId || ''}${d.shopperLoyaltyCardIdCode || ''}`,
              },
              {
                title:  'Issued On',
                field:  'issuedDate',
                width:  '15%',
                render: (d) => (d.issuedDate ? formatDateWithTimetoString(d.issuedDate, true) : '-'),
              },
              {
                title:  'Expiry Date',
                field:  'expiryDate',
                width:  '15%',
                render: (d) => (d.expiryDate ? formatDateWithTimetoString(d.expiryDate, true) : '-'),
              },
              {
                title:  'Voucher Claimed Date',
                field:  'endCardDate',
                width:  '15%',
                render: (d) => (d.endCardDate ? formatDateWithTimetoString(d.endCardDate, true) : '-'),
              },
              {
                title:  'Stamps',
                field:  'stamps',
                width:  '5%',
                render: (d) => (IsNumber(d.stampsCount) ? d.stampsCount : '-'),
              },
              {
                title:  '',
                field:  'actions',
                width:  '5%',
                render: (d) => (
                  <IconButton
                    onClick={() => handleOpenModal(d)}
                    style={{
                      backgroundColor: Colours.Black,
                      borderRadius:    4,
                      width:           32,
                      height:          32,
                      display:         'flex',
                      justifyContent:  'center',
                      alignItems:      'center',
                      padding:         0,
                    }}
                  >
                    <SearchIcon style={{ color: Colours.White, fontSize: 18 }} />
                  </IconButton>
                ),
              },
            ]}
          />
        )}
      </div>

      <Dialog fullWidth maxWidth="xs" open={modalOpen} onClose={handleCloseModal}>
        <DialogContent>
          {selectedItem && (
            <>
              <div className="mt-1 pl-3">
                <div className="mb-3">
                  <MText size={12} bold text="Loyalty Program:" />
                  <MText size={12} text={selectedItem.loyaltyCardName} />
                </div>

                <div className="mb-1">
                  <MText size={12} bold text="Customer Card ID:" />
                  <MText
                    size={12}
                    text={`${selectedItem.loyaltyCardCode || ''}${selectedItem.shopperLoyaltyCardId || ''}${selectedItem.shopperLoyaltyCardIdCode || ''}`}
                  />
                </div>
              </div>

              <Table className="border-collapse w-100">
                <colgroup>
                  <col style={{ width: '31%' }} />
                  <col style={{ width: '69%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell className="font-weight-bold pb-0" style={{ fontSize: 12 }}>Stamp Number</TableCell>
                    <TableCell className="font-weight-bold pb-0" style={{ fontSize: 12 }}>Issued On</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} className="border border-2 border-dark p-0" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItem.stamps && selectedItem.stamps.length > 0 ? (
                    selectedItem.stamps.map((stamp, index) => (
                      <TableRow key={index}>
                        <TableCell className="border-0 py-1" style={{ fontSize: 12 }}>
                          <div className="pl-2">{stamp.stampNum + 1}</div>
                        </TableCell>
                        <TableCell className="border-0 py-1" style={{ fontSize: 12 }}>{formatDateToDayMonthNameYear(stamp.stampIssuedDate, true, '12h')}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="border-0 text-center p-3" style={{ fontSize: 12 }}>No stamps found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MButton
            text="Close"
            onClick={handleCloseModal}
            className="px-4 mr-3 mb-2"
            textColour={Colours.Black}
            colour={Colours.White}
            style={{ border: '1px solid black' }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

LoyaltyCardsCustomers.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};

const mapStateToProps = (state) => ({
  pageSelected: state.page.selected,
});

export default connect(mapStateToProps)(LoyaltyCardsCustomers);
